import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ATID } from "@/lib/adminTestIds";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Users, PlusSquare, Edit3, Trash2, KeyRound, LogOut, ShieldCheck, Copy, Loader2, ScrollText,
} from "lucide-react";
import { formatDateTime } from "@/lib/date";

const ROLE_LABELS = {
  admin: "Administrador",
  director: "Director",
  editor: "Editor",
  periodista: "Periodista",
  colaborador: "Colaborador",
  comercial: "Comercial",
};

const emptyUser = {
  id: "",
  email: "",
  name: "",
  role: "periodista",
  active: true,
  password: "",
  permissions_grant: [],
  permissions_revoke: [],
};

export default function AdminUsersPage() {
  const { user: me, isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rolesCatalog, setRolesCatalog] = useState({ roles: [], permissions: [] });
  const [resetRequests, setResetRequests] = useState([]);
  const [filters, setFilters] = useState({ q: "", role: "", active: "" });

  const [dialog, setDialog] = useState({ open: false, mode: "create", data: emptyUser });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [tempPwd, setTempPwd] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.q) params.q = filters.q;
      if (filters.role) params.role = filters.role;
      if (filters.active !== "") params.active = filters.active === "true";
      const [users, catalog, requests] = await Promise.all([
        api.adminListUsers(params),
        api.adminListRoles(),
        isAdmin || me?.permissions?.includes("users.manage")
          ? api.adminListResetRequests({ status: "pending" }).catch(() => [])
          : Promise.resolve([]),
      ]);
      setItems(users);
      setRolesCatalog(catalog);
      setResetRequests(requests);
    } catch (e) {
      toast.error("No se pudieron cargar los usuarios");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const permissionsByGroup = useMemo(() => {
    const g = {};
    (rolesCatalog.permissions || []).forEach((p) => {
      g[p.group] = g[p.group] || [];
      g[p.group].push(p);
    });
    return g;
  }, [rolesCatalog]);

  const rolePermissions = useMemo(() => {
    const map = {};
    (rolesCatalog.roles || []).forEach((r) => {
      map[r.code] = new Set(r.permissions);
    });
    return map;
  }, [rolesCatalog]);

  const openCreate = () => {
    setDialog({ open: true, mode: "create", data: { ...emptyUser } });
  };

  const openEdit = (u) => {
    setDialog({
      open: true, mode: "edit", data: {
        id: u.id,
        email: u.email,
        name: u.name || "",
        role: u.role,
        active: !!u.active,
        password: "",
        permissions_grant: u.permissions_override?.grant || [],
        permissions_revoke: u.permissions_override?.revoke || [],
      },
    });
  };

  const closeDialog = () => setDialog((d) => ({ ...d, open: false }));

  const handleSubmit = async () => {
    const d = dialog.data;
    if (!d.email || !d.name || !d.role) {
      toast.error("Completá email, nombre y rol");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: d.name,
        role: d.role,
        active: d.active,
        permissions_grant: d.permissions_grant,
        permissions_revoke: d.permissions_revoke,
      };
      let result;
      if (dialog.mode === "create") {
        result = await api.adminCreateUser({ ...payload, email: d.email, password: d.password || null });
        toast.success("Usuario creado");
        if (result?.temp_password) setTempPwd({ email: result.email, password: result.temp_password });
      } else {
        result = await api.adminUpdateUser(d.id, payload);
        toast.success("Usuario actualizado");
      }
      closeDialog();
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar el usuario");
    } finally {
      setSaving(false);
    }
  };

  const handleResetPwd = async (u) => {
    try {
      const r = await api.adminResetUserPassword(u.id, null);
      toast.success("Contraseña restablecida");
      if (r?.temp_password) setTempPwd({ email: u.email, password: r.temp_password });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const handleCloseSessions = async (u) => {
    try {
      await api.adminCloseUserSessions(u.id);
      toast.success("Sesiones cerradas");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const handleToggleActive = async (u) => {
    try {
      await api.adminUpdateUser(u.id, { active: !u.active });
      toast.success(u.active ? "Usuario desactivado" : "Usuario reactivado");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const handleDelete = async (u) => {
    try {
      await api.adminDeleteUser(u.id);
      toast.success("Usuario eliminado");
      setConfirmDelete(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const isCurrentPerm = (code) => {
    const grant = new Set(dialog.data.permissions_grant || []);
    const revoke = new Set(dialog.data.permissions_revoke || []);
    const rolePerms = rolePermissions[dialog.data.role] || new Set();
    if (revoke.has(code)) return false;
    if (grant.has(code)) return true;
    return rolePerms.has(code);
  };

  const togglePerm = (code) => {
    setDialog((d) => {
      const grant = new Set(d.data.permissions_grant || []);
      const revoke = new Set(d.data.permissions_revoke || []);
      const rolePerms = rolePermissions[d.data.role] || new Set();
      const currently = isCurrentPerm(code);
      if (currently) {
        // Revoke it
        grant.delete(code);
        if (rolePerms.has(code)) revoke.add(code);
      } else {
        // Grant it
        revoke.delete(code);
        if (!rolePerms.has(code)) grant.add(code);
      }
      return {
        ...d,
        data: {
          ...d.data,
          permissions_grant: [...grant],
          permissions_revoke: [...revoke],
        },
      };
    });
  };

  return (
    <div data-testid={ATID.usersPage} className="max-w-6xl">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-textgray">Panel</div>
          <h1 className="text-3xl font-black text-nightblue mt-1 font-serif flex items-center gap-2">
            <Users className="w-6 h-6" /> Usuarios
          </h1>
          <p className="text-sm text-textgray mt-1">
            Gestioná roles, permisos personalizados, restablecimientos y sesiones.
          </p>
        </div>
        <Button
          data-testid={ATID.usersNew}
          onClick={openCreate}
          className="bg-nightblue text-white hover:bg-petroleum"
        >
          <PlusSquare className="w-4 h-4 mr-2" /> Nuevo usuario
        </Button>
      </header>

      {/* Filtros */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-6">
        <Input
          data-testid={ATID.usersFilterQ}
          placeholder="Buscar por nombre o email…"
          value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          onKeyDown={(e) => e.key === "Enter" && reload()}
        />
        <Select value={filters.role || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, role: v === "all" ? "" : v }))}>
          <SelectTrigger data-testid={ATID.usersFilterRole}><SelectValue placeholder="Rol" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los roles</SelectItem>
            {rolesCatalog.roles?.map((r) => (
              <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.active === "" ? "all" : filters.active} onValueChange={(v) => setFilters((f) => ({ ...f, active: v === "all" ? "" : v }))}>
          <SelectTrigger data-testid={ATID.usersFilterActive}><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="true">Activos</SelectItem>
            <SelectItem value="false">Inactivos</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={reload}>Aplicar filtros</Button>
      </div>

      {/* Pending reset requests */}
      {resetRequests.length > 0 && (
        <div data-testid={ATID.resetRequestsPanel} className="mb-6 border border-amber-300 bg-amber-50 rounded-md p-4">
          <div className="flex items-center gap-2 font-semibold text-amber-900">
            <KeyRound className="w-4 h-4" /> Pedidos de restablecimiento pendientes ({resetRequests.length})
          </div>
          <ul className="mt-2 divide-y divide-amber-200 text-sm">
            {resetRequests.map((r) => {
              const u = items.find((x) => x.id === r.user_id);
              return (
                <li key={r.id} data-testid={ATID.resetRequestRow(r.id)} className="py-2 flex items-center justify-between gap-2">
                  <div>
                    <div className="font-medium">{r.email}</div>
                    <div className="text-xs text-amber-900/70">{formatDateTime(r.requested_at)}</div>
                  </div>
                  {u && (
                    <Button size="sm" variant="secondary" onClick={() => handleResetPwd(u)}>
                      <KeyRound className="w-3.5 h-3.5 mr-1" /> Generar nueva contraseña
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Users table */}
      <div className="bg-white border border-softblue rounded-md">
        <table data-testid={ATID.usersTable} className="w-full text-sm">
          <thead className="bg-lightgray/60 text-left text-[11px] uppercase tracking-widest text-textgray">
            <tr>
              <th className="py-3 px-4">Usuario</th>
              <th className="py-3 px-4">Rol</th>
              <th className="py-3 px-4">Estado</th>
              <th className="py-3 px-4">Último acceso</th>
              <th className="py-3 px-4 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="p-6 text-center text-textgray"><Loader2 className="inline w-4 h-4 animate-spin mr-2" /> Cargando…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-textgray">No hay usuarios.</td></tr>
            )}
            {items.map((u) => (
              <tr key={u.id} data-testid={ATID.userRow(u.id)} className="border-t border-softblue/60">
                <td className="px-4 py-3">
                  <div className="font-medium text-nightblue">{u.name || "—"}</div>
                  <div className="text-xs text-textgray">{u.email}</div>
                </td>
                <td className="px-4 py-3 uppercase text-[11px] tracking-widest">{ROLE_LABELS[u.role] || u.role}</td>
                <td className="px-4 py-3">
                  {u.active ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold">
                      <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" /> Activo
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-rose-700 text-xs font-semibold">
                      <span className="w-2 h-2 rounded-full bg-rose-500 inline-block" /> Inactivo
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-textgray">
                  {u.last_login_at ? formatDateTime(u.last_login_at) : "—"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap justify-end gap-1.5">
                    <Button data-testid={ATID.userEdit(u.id)} size="sm" variant="outline" onClick={() => openEdit(u)}>
                      <Edit3 className="w-3.5 h-3.5 mr-1" /> Editar
                    </Button>
                    <Button data-testid={ATID.userReset(u.id)} size="sm" variant="outline" onClick={() => handleResetPwd(u)}>
                      <KeyRound className="w-3.5 h-3.5 mr-1" /> Reset
                    </Button>
                    <Button data-testid={ATID.userCloseSessions(u.id)} size="sm" variant="outline" onClick={() => handleCloseSessions(u)}>
                      <LogOut className="w-3.5 h-3.5 mr-1" /> Sesiones
                    </Button>
                    <Button data-testid={ATID.userToggleActive(u.id)} size="sm" variant="outline" onClick={() => handleToggleActive(u)}
                      disabled={u.id === me?.id}>
                      {u.active ? "Desactivar" : "Reactivar"}
                    </Button>
                    <Button data-testid={ATID.userDelete(u.id)} size="sm" variant="destructive" onClick={() => setConfirmDelete(u)}
                      disabled={u.id === me?.id}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dialog.open} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent data-testid={ATID.userDialog} className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{dialog.mode === "create" ? "Crear usuario" : "Editar usuario"}</DialogTitle>
            <DialogDescription>
              Asigná un rol y ajustá permisos concretos si es necesario. Los cambios de rol o permisos cierran las sesiones abiertas.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Email</Label>
              <Input
                data-testid={ATID.userDialogEmail}
                type="email"
                value={dialog.data.email}
                disabled={dialog.mode === "edit"}
                onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, email: e.target.value } }))}
              />
            </div>
            <div>
              <Label>Nombre visible</Label>
              <Input
                data-testid={ATID.userDialogName}
                value={dialog.data.name}
                onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, name: e.target.value } }))}
              />
            </div>
            <div>
              <Label>Rol</Label>
              <Select
                value={dialog.data.role}
                onValueChange={(v) => setDialog((d) => ({ ...d, data: { ...d.data, role: v, permissions_grant: [], permissions_revoke: [] } }))}
              >
                <SelectTrigger data-testid={ATID.userDialogRole}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {rolesCatalog.roles?.map((r) => (
                    <SelectItem key={r.code} value={r.code} disabled={r.code === "admin" && !isAdmin}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-xs text-textgray mt-1">
                {rolesCatalog.roles?.find((r) => r.code === dialog.data.role)?.description}
              </div>
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch
                data-testid={ATID.userDialogActive}
                checked={dialog.data.active}
                onCheckedChange={(v) => setDialog((d) => ({ ...d, data: { ...d.data, active: v } }))}
              />
              <span className="text-sm">{dialog.data.active ? "Usuario activo" : "Usuario desactivado"}</span>
            </div>
            {dialog.mode === "create" && (
              <div className="sm:col-span-2">
                <Label>Contraseña inicial (opcional)</Label>
                <Input
                  data-testid={ATID.userDialogPassword}
                  type="text"
                  placeholder="Dejalo vacío para generar una automática"
                  value={dialog.data.password}
                  onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, password: e.target.value } }))}
                />
                <div className="text-xs text-textgray mt-1">
                  Si la generamos automáticamente, se te mostrará una única vez para que la compartas por un canal seguro.
                </div>
              </div>
            )}
          </div>

          {/* Permisos */}
          <div className="mt-2 border-t pt-4">
            <div className="text-sm font-semibold text-nightblue mb-3 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Permisos efectivos
            </div>
            <div className="space-y-4">
              {Object.entries(permissionsByGroup).map(([group, perms]) => (
                <div key={group}>
                  <div className="text-[11px] uppercase tracking-widest text-textgray mb-2">{group}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {perms.map((p) => {
                      const active = isCurrentPerm(p.code);
                      const rolePerms = rolePermissions[dialog.data.role] || new Set();
                      const isDefault = rolePerms.has(p.code);
                      return (
                        <button
                          key={p.code}
                          type="button"
                          data-testid={ATID.userDialogPermToggle(p.code)}
                          onClick={() => togglePerm(p.code)}
                          className={`text-left border rounded-md p-2.5 text-xs transition-colors ${
                            active
                              ? "border-watergreen bg-watergreen/10 text-nightblue"
                              : "border-softblue bg-white text-textgray hover:border-nightblue"
                          }`}
                        >
                          <div className="font-medium">{p.label}</div>
                          <div className="text-[10px] uppercase tracking-widest mt-1 opacity-70">
                            {p.code} · {isDefault ? "por rol" : "personalizado"}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={closeDialog}>Cancelar</Button>
            <Button
              data-testid={ATID.userDialogSubmit}
              onClick={handleSubmit}
              disabled={saving}
              className="bg-nightblue text-white hover:bg-petroleum"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {dialog.mode === "create" ? "Crear usuario" : "Guardar cambios"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Temp password reveal */}
      <Dialog open={!!tempPwd} onOpenChange={(o) => !o && setTempPwd(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Contraseña temporal generada</DialogTitle>
            <DialogDescription>
              Compartila por un canal seguro. Solo la vas a poder ver una vez. Al ingresar, el usuario deberá cambiarla.
            </DialogDescription>
          </DialogHeader>
          {tempPwd && (
            <div className="space-y-3">
              <div className="text-xs uppercase tracking-widest text-textgray">Email</div>
              <div className="font-medium">{tempPwd.email}</div>
              <div className="text-xs uppercase tracking-widest text-textgray">Contraseña</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-lightgray p-3 rounded font-mono text-base">{tempPwd.password}</code>
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(tempPwd.password); toast.success("Copiada"); }}>
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copiar
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setTempPwd(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar usuario</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción elimina la cuenta de <strong>{confirmDelete?.email}</strong>. No se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700"
              onClick={() => confirmDelete && handleDelete(confirmDelete)}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
